import React, { useState, useEffect } from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { TaskStatusKey, MeetingStatusKey, taskStatusColors, meetingStatusColors } from "../../utils/statusColors";
import { fromServerToLocal } from "../../utils/time";
import { isOfflineActionAvailable, getOfflineMessage } from "../../utils/offlineGuard";
import { getCachedStatuses } from "../../storage/preloadData";
import { updateTask } from "../../services/taskService";
import { updateMeeting } from "../../services/meetingService";
import StatusChangeModal, { StatusType } from "../Modals/StatusChangeModal";
import Notification from "../Common/Notification";

type StatusKey = "all" | TaskStatusKey | MeetingStatusKey;

export interface ItemWithSource {
  id: string;
  name: string;
  assignedUserName?: string;
  status: StatusKey;
  dateStart: string;
  dateEnd: string;
  description?: string;
  source: "task" | "meeting";
  cSolution?: string | null;
  resolution?: string;
}

export interface ItemCardProps {
  item: ItemWithSource;
  getStatusConfig: (
    status: StatusKey,
    source: "task" | "meeting"
  ) => { color: string; translation: string };
  customNavigate?: (item: ItemWithSource) => string;
  onStatusChange?: () => void;
  onStatusUpdate?: (newStatus: string, resolution?: string) => Promise<void>;
  onStatusUpdateSuccess?: (message: string) => void;
  statusTypeOverride?: StatusType;
  availableStatusesOverride?: string[];
  statusColorsOverride?: Record<string, string>;
  currentStatusOverride?: string;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  getStatusConfig,
  customNavigate,
  onStatusChange,
  onStatusUpdate,
  onStatusUpdateSuccess,
  statusTypeOverride,
  availableStatusesOverride,
  statusColorsOverride,
  currentStatusOverride,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const statusConfig = getStatusConfig(item.status, item.source);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [offlineNotification, setOfflineNotification] = useState<string | null>(null);
  const [isClickable, setIsClickable] = useState(true);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dateFormat =
    i18n.language === "en_US" ? "MM/DD/YYYY HH:mm" : "DD.MM.YYYY HH:mm";

  const startLocal = fromServerToLocal(item.dateStart);
  const endLocal = fromServerToLocal(item.dateEnd);

  const formattedStart =
    startLocal && dayjs(startLocal).isValid()
      ? startLocal.format(dateFormat)
      : "-";

  const formattedEnd =
    endLocal && dayjs(endLocal).isValid() ? endLocal.format(dateFormat) : "-";

  useEffect(() => {
    if (availableStatusesOverride !== undefined) {
      return;
    }

    const loadStatuses = async () => {
      try {
        const statuses = await getCachedStatuses();
        const itemStatuses =
          item.source === "task" ? statuses.tasks : statuses.meetings;
        if (itemStatuses.length > 0) {
          setAvailableStatuses(itemStatuses);
        } else {
          const defaultStatuses =
            item.source === "task"
              ? ["Not Started", "Started", "Completed", "Deferred", "Сanceled"]
              : ["Planned", "Held", "Not Held"];
          setAvailableStatuses(defaultStatuses);
        }
      } catch (err) {
        console.error("Failed to load statuses:", err);
        const defaultStatuses =
          item.source === "task"
            ? ["Not Started", "Started", "Completed", "Deferred", "Сanceled"]
            : ["Planned", "Held", "Not Held"];
        setAvailableStatuses(defaultStatuses);
      }
    };
    loadStatuses();
  }, [item.source, availableStatusesOverride]);

  const resolvedAvailableStatuses =
    availableStatusesOverride ?? availableStatuses;

  const statusColorsMap: Record<string, string> =
    statusColorsOverride ||
    (item.source === "task"
      ? Object.keys(taskStatusColors).reduce(
          (acc, key) => {
            acc[key] = taskStatusColors[key as TaskStatusKey].color;
            return acc;
          },
          {} as Record<string, string>,
        )
      : Object.keys(meetingStatusColors).reduce(
          (acc, key) => {
            acc[key] = meetingStatusColors[key as MeetingStatusKey].color;
            return acc;
          },
          {} as Record<string, string>,
        ));

  const handleStatusSave = async (newStatus: string, resolution?: string) => {
    setIsLoading(true);
    try {
      if (onStatusUpdate) {
        await onStatusUpdate(newStatus, resolution);
      } else if (item.source === "task") {
        const updateData: any = { status: newStatus };
        if (newStatus === "Completed") {
          updateData.cSolution = resolution || "";
        }
        await updateTask(item.id, updateData);
      } else if (item.source === "meeting") {
        await updateMeeting(item.id, { status: newStatus });
      }
      setStatusModalOpen(false);
      if (onStatusUpdateSuccess) {
        onStatusUpdateSuccess(t("statusChangeModal.success") || "Статус успешно изменен");
      }
      if (onStatusChange) {
        onStatusChange();
      } else {
        window.location.reload();
      }
    } catch (err: any) {
      console.error("Failed to update status:", err);
      setOfflineNotification(err.message || "Ошибка при обновлении статуса");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setStatusModalOpen(true);
  };

  const handleClick = async () => {
    const { available, reason } = await isOfflineActionAvailable("view", t);
    
    if (!available) {
      setOfflineNotification(reason || getOfflineMessage("view", t));
      setIsClickable(false);
      setTimeout(() => {
        setIsClickable(true);
      }, 3000);
      return;
    }

    const url = customNavigate ? customNavigate(item) : `/${item.source}/${item.id}`;
    navigate(url);
  };

  const cardContent = (
    <CardContent sx={{ p: { xs: 1, sm: 1.5 }, position: "relative" }}>
      {isMobile && (
        <Chip
          label={statusConfig.translation}
          onClick={handleStatusClick}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: "100px",
            backgroundColor: statusConfig.color,
            color: "#fff",
            fontSize: "0.6rem",
            fontWeight: "bold",
            borderRadius: "24px",
            py: 0,
            px: 0,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Box sx={{ flex: { sm: "0 0 20%" }, maxWidth: { sm: "20%" } }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "medium",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.name}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 20%" },
            maxWidth: { sm: "20%" },
            textAlign: { sm: "center" },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            {item.assignedUserName || "-"}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 20%" },
            maxWidth: { sm: "20%" },
            textAlign: { sm: "center" },
            display: { xs: "none", sm: "block" },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { sm: "0.875rem" } }}
          >
            {formattedStart}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 20%" },
            maxWidth: { sm: "20%" },
            textAlign: { sm: "center" },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            {isMobile
              ? `${t("whenRange.to")} ${formattedEnd}`
              : formattedEnd}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 20%" },
            maxWidth: { sm: "20%" },
            textAlign: { sm: "center" },
            display: { xs: "none", sm: "flex" },
          }}
        >
          <Chip
            label={statusConfig.translation}
            onClick={handleStatusClick}
            sx={{
              width: { sm: "140px" },
              backgroundColor: statusConfig.color,
              color: "#fff",
              borderRadius: "16px",
              py: 0.5,
              fontSize: { sm: "0.75rem" },
              fontWeight: "bold",
              textAlign: "center",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          />
        </Box>
      </Box>
    </CardContent>
  );

  const isOffline = !navigator.onLine;
  const offlineMessage = getOfflineMessage("view", t);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          mb: 1,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          border: "none",
          opacity: isOffline && !isClickable ? 0.6 : 1,
          cursor: isOffline && !isClickable ? "not-allowed" : "pointer",
        }}
      >
        {isOffline && !isClickable ? (
          <Tooltip title={offlineMessage} arrow>
            <Box>{cardContent}</Box>
          </Tooltip>
        ) : (
          <CardActionArea onClick={handleClick} disabled={!isClickable}>
            {cardContent}
          </CardActionArea>
        )}
      </Card>
      <Notification
        open={!!offlineNotification}
        message={offlineNotification || ""}
        severity="warning"
        onClose={() => setOfflineNotification(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
      <StatusChangeModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSave={handleStatusSave}
        currentStatus={currentStatusOverride ?? item.status}
        availableStatuses={resolvedAvailableStatuses}
        statusType={
          statusTypeOverride || (item.source === "task" ? "task" : "meeting")
        }
        statusColors={statusColorsMap}
        isLoading={isLoading}
        currentResolution={item.source === "task" ? (item.cSolution || item.resolution) : undefined}
      />
    </>
  );
};

export default ItemCard;
